/**
 * Wavefront OBJ: enough of it to measure a model, not to reproduce one.
 *
 * OBJ carries materials, texture coordinates, smoothing groups, named objects and free-form
 * surfaces. None of that changes the shape's extent or how many triangles it takes to draw,
 * which is all this layer is asked for — the renderer builds geometry with three.js's own
 * loader. So this reads the two line types that matter and skips the rest.
 *
 * ## What is deliberately not honoured
 *
 * A file may declare several named objects with separate materials. They are measured as
 * one, because the viewer shows one shape per library entry and colours it as a whole.
 *
 * OBJ also has no units. Every other format here is taken to be millimetres and so is this,
 * which is a guess — the format genuinely does not say, and a model exported in metres will
 * appear a thousand times too small. There is no way to tell from the file which was meant.
 */

import { BoundsAccumulator } from './bounds.js';
import type { ModelStats } from './types.js';

export type ObjProblem = 'no-vertices' | 'no-faces' | 'non-finite-vertex';

export type ObjReading =
  | { readonly ok: true; readonly stats: ModelStats }
  | { readonly ok: false; readonly problem: ObjProblem; readonly message: string };

export function readObj(text: string): ObjReading {
  const bounds = new BoundsAccumulator();
  let vertexCount = 0;
  let triangleCount = 0;

  for (const rawLine of text.split('\n')) {
    // `#` to end of line is OBJ's only comment form.
    const hash = rawLine.indexOf('#');
    const line = (hash === -1 ? rawLine : rawLine.slice(0, hash)).trim();
    if (line === '') continue;

    // Exactly `v`, not `vn` or `vt`: normals and texture coordinates are not positions,
    // and a normal read as a vertex would pull the bounds towards the unit cube.
    if (line.startsWith('v ') || line.startsWith('v\t')) {
      const parts = line.slice(1).trim().split(/\s+/);
      const included = bounds.add(
        Number(parts[0]),
        Number(parts[1]),
        Number(parts[2] ?? Number.NaN),
      );
      if (!included) {
        return fail(
          'non-finite-vertex',
          'The file contains a vertex that is not a finite number, so it cannot be rendered.',
        );
      }
      vertexCount += 1;
      continue;
    }

    if (line.startsWith('f ') || line.startsWith('f\t')) {
      // A face may have any number of corners; a fan of n corners is n - 2 triangles,
      // which is how every renderer will draw it.
      const corners = line.slice(1).trim().split(/\s+/).length;
      if (corners >= 3) triangleCount += corners - 2;
    }
  }

  if (vertexCount === 0) {
    return fail('no-vertices', 'No vertices were found, so there is nothing to show.');
  }
  if (triangleCount === 0) {
    return fail(
      'no-faces',
      'The file has vertices but no faces, so there is no surface to draw.',
    );
  }

  return {
    ok: true,
    stats: { kind: 'obj', encoding: 'ascii', triangleCount, bounds: bounds.bounds() },
  };
}

/**
 * Does this look like OBJ?
 *
 * Wants a vertex line and a face line, in that order of likelihood. Vertices alone are not
 * enough: several formats write `v` lines, and a file with no faces is not one this viewer
 * can draw anyway.
 */
export function looksLikeObj(text: string): boolean {
  if (text.includes('\0')) return false;

  let sawVertex = false;
  for (const rawLine of text.split('\n')) {
    const line = rawLine.trim();
    if (line.startsWith('v ')) sawVertex = true;
    if (sawVertex && line.startsWith('f ')) return true;
  }

  return false;
}

function fail(problem: ObjProblem, message: string): ObjReading {
  return { ok: false, problem, message };
}
